import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { Icon, IconButton } from '@mui';
import './toolbar.scss';

const Toolbar = ({
  DateTimeFormat,
  displayDate,
  locale,
  nextMonth = true,
  onMonthChange,
  prevMonth = true,
  showNextMonth,
  showPrevMonth
}) => {
  const [transitionDirection, setTransitionDirection] = useState('left');
  const prevDisplayDateRef = useRef(displayDate);

  // Track direction based on date change
  useEffect(() => {
    if (prevDisplayDateRef.current !== displayDate) {
      // isRtl is always false in this project
      const direction = displayDate > prevDisplayDateRef.current ? 'left' : 'right';
      setTransitionDirection(direction);
      prevDisplayDateRef.current = displayDate;
    }
  }, [displayDate]);

  const handleTouchTapPrevMonth = useCallback(() => {
    if (onMonthChange) {
      onMonthChange(-1);
    }
  }, [onMonthChange]);

  const handleTouchTapNextMonth = useCallback(() => {
    if (onMonthChange) {
      onMonthChange(1);
    }
  }, [onMonthChange]);

  const dateTimeFormatted = new DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric'
  }).format(displayDate);

  // isRtl is always false in this project
  const nextButtonIcon = <Icon aria-hidden="true">chevron_right</Icon>;
  const prevButtonIcon = <Icon aria-hidden="true">chevron_left</Icon>;

  return (
    <div styleName="custom-calendar-toolbar-container">
      {showPrevMonth && (
        <IconButton
          disabled={!prevMonth}
          onClick={handleTouchTapPrevMonth}
          aria-label="Previous month"
        >
          {prevButtonIcon}
        </IconButton>
      )}
      <TransitionGroup
        styleName="custom-calendar-toolbar-title-container"
        childFactory={child => React.cloneElement(child, {
          classNames: `slide-${transitionDirection}`
        })}
      >
        <CSSTransition
          key={dateTimeFormatted}
          timeout={450}
          classNames={`slide-${transitionDirection}`}
        >
          <div styleName="custom-calendar-toolbar-title-text">
            {dateTimeFormatted}
          </div>
        </CSSTransition>
      </TransitionGroup>
      {showNextMonth && (
        <IconButton
          disabled={!nextMonth}
          onClick={handleTouchTapNextMonth}
          aria-label="Next month"
        >
          {nextButtonIcon}
        </IconButton>
      )}
    </div>
  );
};

Toolbar.propTypes = {
  DateTimeFormat: PropTypes.func.isRequired,
  displayDate: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  nextMonth: PropTypes.bool,
  onMonthChange: PropTypes.func,
  prevMonth: PropTypes.bool,
  showNextMonth: PropTypes.bool,
  showPrevMonth: PropTypes.bool
};

export default Toolbar;
